import type { OHLCVData, Timeframe } from '../types'

// Generate mock OHLCV data for demo purposes
export function generateMockData(symbol: string, timeframe: Timeframe, count: number): OHLCVData[] {
  const data: OHLCVData[] = []
  const now = Math.floor(Date.now() / 1000)
  
  // Time intervals in seconds
  const intervals: Record<Timeframe, number> = {
    '1m': 60,
    '5m': 300,
    '15m': 900,
    '30m': 1800,
    '1h': 3600,
    '4h': 14400,
    '1d': 86400,
    '1w': 604800,
    '1M': 2592000,
  }
  
  const interval = intervals[timeframe]
  
  // Base prices for different symbols
  const basePrices: Record<string, number> = {
    'BTC/USDT': 67000,
    'ETH/USDT': 3500,
    'SOL/USDT': 140,
    'BNB/USDT': 580,
    'XRP/USDT': 0.52,
    'ADA/USDT': 0.45,
    'DOGE/USDT': 0.08,
    'DOT/USDT': 7.2,
    'AVAX/USDT': 35,
    'MATIC/USDT': 0.85,
    'EUR/USD': 1.085,
    'GBP/USD': 1.265,
    'USD/JPY': 149.5,
    'USD/CHF': 0.88,
    'AUD/USD': 0.655,
    'EUR/GBP': 0.858,
    'EUR/JPY': 162.2,
    'AAPL': 185,
    'GOOGL': 140,
    'MSFT': 415,
    'AMZN': 178,
    'TSLA': 245,
    'NVDA': 875,
    'META': 485,
    'SPX': 5100,
    'NDX': 18000,
    'DJI': 38500,
    'XAUUSD': 2340,
    'XAGUSD': 27.5,
    'WTIUSD': 78,
  }
  
  let price = basePrices[symbol] || 100
  
  // Volatility based on asset type
  let volatility = 0.02 // 2% default
  if (symbol.includes('BTC') || symbol.includes('ETH')) {
    volatility = 0.03
  } else if (symbol.includes('SOL') || symbol.includes('DOGE')) {
    volatility = 0.05
  } else if (symbol.includes('USD') && !symbol.includes('XAU')) {
    volatility = 0.005 // Forex is less volatile
  }
  
  // Generate a trend
  let trend = (Math.random() - 0.5) * 0.001 // Slight random trend
  
  for (let i = 0; i < count; i++) {
    const time = now - (count - i) * interval
    
    // Random walk with trend
    const change = (Math.random() - 0.5) * volatility + trend
    price = price * (1 + change)
    
    // Occasionally change trend
    if (Math.random() < 0.02) {
      trend = (Math.random() - 0.5) * 0.001
    }
    
    // Generate OHLC from the close price
    const bodySize = price * volatility * Math.random() * 0.5
    const wickSize = price * volatility * Math.random() * 0.3
    
    const isGreen = Math.random() > 0.5
    const open = isGreen ? price - bodySize : price + bodySize
    const close = price
    const high = Math.max(open, close) + wickSize
    const low = Math.min(open, close) - wickSize
    
    // Volume (higher on big moves)
    const baseVolume = 1000000
    const volumeMultiplier = 1 + Math.abs(change) * 50
    const volume = baseVolume * volumeMultiplier * (0.5 + Math.random())
    
    data.push({
      time,
      open: roundPrice(open, symbol),
      high: roundPrice(high, symbol),
      low: roundPrice(low, symbol),
      close: roundPrice(close, symbol),
      volume: Math.round(volume),
    })
  }
  
  return data
}

function roundPrice(price: number, symbol: string): number {
  // Different precision for different assets
  if (symbol.includes('BTC') || symbol.includes('ETH')) {
    return Math.round(price * 100) / 100
  } else if (symbol.includes('XRP') || symbol.includes('ADA') || symbol.includes('DOGE')) {
    return Math.round(price * 10000) / 10000
  } else if (symbol.includes('USD') && symbol.length === 7) {
    return Math.round(price * 100000) / 100000 // Forex
  } else if (symbol.includes('XAU') || symbol.includes('XAG')) {
    return Math.round(price * 100) / 100
  } else {
    return Math.round(price * 100) / 100
  }
}

// Generate real-time price updates
export function generatePriceTick(basePrice: number, volatility: number = 0.001): number {
  const change = (Math.random() - 0.5) * volatility
  return basePrice * (1 + change)
}
